import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { useEscapeLayer } from '../escStack';
import { Chip } from './Chip';
import { Icon } from './Icon';
import s from './Menu.module.css';

export type MenuOption<V extends string> = { value: V; label: ReactNode };

/**
 * A toolbar dropdown chip («Тариф: все ▾») with its option list. The design draws the chip only; the list
 * is additive (P1-F01), in the design's language: a #151517 card, .08 border, radius 16, 36px pill rows,
 * the selected row in the accent tint. Arrow keys move, Enter picks, Esc and an outside click close.
 */
export function DropdownChip<V extends string>({
  label,
  value,
  options,
  onChange,
  menuLabel,
}: {
  /** The chip's text for the current value. */
  label: ReactNode;
  value: V;
  options: MenuOption<V>[];
  onChange: (v: V) => void;
  /** Accessible name of the list. */
  menuLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const id = useId();
  useEscapeLayer(open, () => setOpen(false));

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(() => {
    if (open) list.current?.querySelectorAll<HTMLElement>('[role="option"]')[active]?.focus();
  }, [open, active]);

  const toggle = () => {
    setActive(Math.max(0, options.findIndex((o) => o.value === value)));
    setOpen((v) => !v);
  };
  const pick = (v: V) => {
    onChange(v);
    setOpen(false);
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') setActive((i) => (i + 1) % options.length);
    else if (e.key === 'ArrowUp') setActive((i) => (i - 1 + options.length) % options.length);
    else if (e.key === 'Home') setActive(0);
    else if (e.key === 'End') setActive(options.length - 1);
    else if (e.key === 'Tab') setOpen(false);
    else return;
    if (e.key !== 'Tab') e.preventDefault();
  };

  return (
    <div className={s.root} ref={root}>
      <Chip variant="dropdown" aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? id : undefined} onClick={toggle}>
        {label}
      </Chip>
      {open && (
        <div ref={list} id={id} role="listbox" aria-label={menuLabel} className={s.menu} onKeyDown={onKey}>
          {options.map((o, i) => {
            const on = o.value === value;
            return (
              <div
                key={o.value}
                role="option"
                aria-selected={on}
                tabIndex={i === active ? 0 : -1}
                className={cx(s.item, on && s.on)}
                onClick={() => pick(o.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    pick(o.value);
                  }
                }}
              >
                <span className={s.itemLabel}>{o.label}</span>
                {on && <Icon name="check" size={14} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
